import { Dispatch, PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Disease, DiseaseListItem, GetDiseasesParams, PaginatedResponse, Pagination, typicodeApi } from 'shared/api';

type TDiseaseState = {
  list: Disease[];
  diseaseList: DiseaseListItem[];
  pagination: Pagination | null;
};

const initialState: TDiseaseState = {
  list: [],
  diseaseList: [],
  pagination: null,
};

export const diseasePetsModel = createSlice({
  name: 'diseases-pets',
  initialState,
  reducers: {
    setDiseases: (state, { payload }: PayloadAction<Disease[]>) => {
      state.list = payload;
    },
    setDiseaseList: (state, { payload }: PayloadAction<DiseaseListItem[]>) => {
      state.diseaseList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    addDiseases: (state, { payload }: PayloadAction<Disease>) => {
      state.list = [...state.list, payload];
    },
    updateDiseases: (state, { payload }: PayloadAction<Disease>) => {
      state.list = state.list.map((item) => (item._id === payload._id ? payload : item));
    },
  },
});

// react-query actions (everything that async)
export const DISEASES_PETS_ALL_QUERY_KEY = 'diseases-pets-all';
const DISEASE_PETS_BY_ID_QUERY_KEY = 'disease-pets-by-id';

export const getDiseasesPetsAllAsync = (params: GetDiseasesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<DiseaseListItem>>>(
    [DISEASES_PETS_ALL_QUERY_KEY, params],
    () => typicodeApi.diseasePets.getDiseasesAll(params),
    {
      onSuccess: ({ data }) => {
        dispatch(diseasePetsModel.actions.setDiseaseList(data.data));
        dispatch(diseasePetsModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('There was an error loading the list of diseases, try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getDiseasePetsByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<Disease>>(
    [DISEASE_PETS_BY_ID_QUERY_KEY, id],
    () => typicodeApi.diseasePets.getDiseaseById(id),
    {
      enabled,
      staleTime: 0, // Данные сразу считаются устаревшими - всегда свежие при открытии
      cacheTime: 0, // Не кешировать - каждый раз загружать заново
      refetchOnWindowFocus: false,
    },
  );

export const postNewDiseasePetsAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.diseasePets.postDisease, {
    onSuccess: () => {
      toast.success('Disease added successfully!');
      void queryClient.invalidateQueries(DISEASES_PETS_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when adding a new disease. Try again!');
    },
  });
};

export const updateDiseasePetsAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.diseasePets.patchDisease, {
    onSuccess: () => {
      toast.success('The disease has been successfully changed!');
      void queryClient.invalidateQueries(DISEASES_PETS_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when changing the disease. Try again!');
    },
  });
};

// selectors
export const useDiseasesPets = () => useSelector((state: RootState) => state.diseasePets);
export const useDiseasePetsList = () =>
  useSelector((state: RootState) => ({
    diseaseList: state.diseasePets.diseaseList,
    pagination: state.diseasePets.pagination,
  }));
export const isDiseasesEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.diseasePets.list,
      (diseasePets) => diseasePets.length === 0,
    ),
  );
export const isDiseasePetsListEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.diseasePets.diseaseList,
      (diseases) => diseases.length === 0,
    ),
  );
export const reducerPets = diseasePetsModel.reducer;
